<template>
	<AppLayout
		:title="trans('enrollments.bundles.create.title')"
		:subtitle="trans('enrollments.bundles.create.subtitle')"
		:return-url="route('events.enrollments.show', enrollment.id)"
		:return-label="trans('enrollments.id', {id: enrollment.id})"
	>
		<p class="text-gray-400 text-sm">{{ trans('enrollments.bundles.create.help') }}</p>
		<Alert v-if="shoppingStage.refundable" :color="Color.Blue">{{ trans('enrollments.bundles.create.refundable') }}</Alert>
		<Alert v-else :color="Color.Amber">{{ trans('enrollments.bundles.create.nonrefundable') }}</Alert>
		<div class="grid grid-cols-1 md:grid-cols-9 gap-4">
			<div class="md:col-span-5 lg:col-span-6 flex flex-col gap-4">
				<Empty v-if="bundles.length === 0"/>
				<template v-for="bundle in bundles" :key="`bundle-${bundle.id}`">
					<Heading>{{ bundle.title }}</Heading>
					<div v-if="bundle.description" class="wysiwyg-content" v-html="bundle.description"/>
					<template v-for="offer in bundle.offers" :key="`offer-${offer.id}`">
						<SelectOffer v-if="!previouslySelectedOfferIds.includes(offer.id)" :offer="offer" :selected-offers="selectedOffers" :show-availability="shoppingStage.show_availability" @selected="toggle"/>
					</template>
				</template>
			</div>
			<div class="md:col-span-4 lg:col-span-3">
				<Card class="sticky top-16" :title="trans('enrollments.summary.title')">
					<div v-for="offer in selectedOffers" class="flex">
						<p class="grow">{{ offer.title }}</p>
						<p>$ {{ formatNumber(offer.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<hr>
					<div class="flex font-bold">
						<p class="grow">{{ trans('enrollments.invoice.total') }}</p>
						<p>$ {{ formatNumber(total, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
					</div>
					<p v-if="!canProceed" class="text-gray-400">{{ trans('enrollments.bundles.create.cannot_proceed') }}</p>
					<PrimaryButton :color="Color.Green" data-testid="proceed-button" :disabled="!canProceed" @click.stop="proceed">
						<i class="fa-solid fa-check mr-2"/>
						{{ trans('general.proceed') }}
					</PrimaryButton>
				</Card>
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {useForm} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/Empty.vue'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Alert from '@/Components/Alerts/Alert.vue'
import {type Bundle} from '@/Types/Events/Bundles/Bundle'
import Heading from '@/Components/Typography/Heading.vue'
import {computed, type ComputedRef, ref, type Ref} from 'vue'
import {type Offer} from '@/Types/Events/Bundles/Offers/Offer'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SelectOffer from '@/Components/Enrollments/SelectOffer.vue'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'
import {type ShoppingStage} from '@/Types/Events/Stages/ShoppingStage'
import {formatNumber, showInfo, showSuccess, showValidationErrors} from '@/helpers'

const props = defineProps<{
	bundles:Array<Bundle>,
	enrollment:Enrollment,
	shoppingStage:ShoppingStage,
	previouslySelectedOfferIds:Array<number>,
}>()

const sending:Ref<boolean> = ref(false)
const bundles:Ref<Array<Bundle>> = ref(props.bundles)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const selectedOffers:Ref<Array<Offer>> = ref([])
const shoppingStage:Ref<ShoppingStage> = ref(props.shoppingStage)
const previouslySelectedOfferIds:Ref<Array<number>> = ref(props.previouslySelectedOfferIds)


const subtotal:ComputedRef<number> = computed(() => {
	let subtotal = 0
	for(let i = 0; i < selectedOffers.value.length; i++){
		subtotal += selectedOffers.value[i].price
	}
	return subtotal
})
const total:ComputedRef<number> = computed(() => {
	return subtotal.value
})
const canProceed:ComputedRef<boolean> = computed(() => {
	return selectedOffers.value.length > 0
})

function toggle(offer:Offer):void{
	const index = selectedOffers.value.findIndex((o:Offer) => o.id === offer.id)
	if(index === -1){
		selectedOffers.value.push(offer)
	} else {
		selectedOffers.value.splice(index, 1)
	}
}

function proceed():void{
	if(!sending.value && canProceed.value){
		showInfo(trans('general.updating'))
		sending.value = true
		useForm({
			offers: selectedOffers.value.map((offer:Offer) => offer.id),
		}).post(route('events.enrollments.bundles.offers.store', enrollment.value.id), {
			onSuccess: () => {
				showSuccess(trans('general.updated'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				sending.value = false
			},
		})
	}
}
</script>
